import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import useForm from '../../hooks/useForm';
import imageCompression from "browser-image-compression";
import axios from 'axios';

const EditBulletinModal = ({ isOpen, onClose, onSuccess, bulletin }) => {
    const API_URL = process.env.REACT_APP_API_URL;
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [removedImages, setRemovedImages] = useState([]);
    
    const validate = async (values) => {
        const errors = {};
        if (!values.header) errors.header = "Please enter header";
        if (!values.event_date) errors.event_date = "Please enter event_date";
        if (!values.body) errors.body = "Please enter body";
        
        // Only require images if there are no existing images or all existing images have been removed
        if (imageFiles.length === 0 && existingImages.length === 0) {
            errors.file_name = "Please upload at least one image";
        }
        
        return errors;
    };

    const { values, errors, isSubmitting, handleChange, handleSubmit, setValues } = useForm(
        {
            header: '',
            event_date: '',
            body: '',
            bulletin_status: ''
        },
        validate
    );

    useEffect(() => {
        if (bulletin) {
            let formattedDate = '';
            if (bulletin.event_date) {
                // Convert ISO date string to YYYY-MM-DD format
                const date = new Date(bulletin.event_date);
                formattedDate = date.toISOString().split('T')[0];
            }
        
            setValues({
                header: bulletin.header_name || '',
                event_date: formattedDate,
                body: bulletin.description || '',
                bulletin_status: bulletin.bulletin_status !== undefined ? String(bulletin.bulletin_status) : ''
            });
            
            // Handle existing images by parsing the comma-separated string
            if (bulletin.file_name) {
                const imageNames = bulletin.file_name.split(',');
                // Create an array of image objects from the file names
                const images = imageNames.map(name => ({ file_name: name.trim() }));
                setExistingImages(images);
            }
        }
    }, [bulletin, setValues]);

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            try {
                const options = {
                    maxSizeMB: 1,
                    maxWidthOrHeight: 1024,
                    useWebWorker: true,
                };

                const processImage = async (file) => {
                    const compressedFile = await imageCompression(file, options);
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.src = URL.createObjectURL(compressedFile);
                        img.onload = () => {
                            const canvas = document.createElement("canvas");
                            const ctx = canvas.getContext("2d");
                            canvas.width = img.width;
                            canvas.height = img.height;
                            ctx.drawImage(img, 0, 0, img.width, img.height);
                            const webpDataUrl = canvas.toDataURL("image/webp", 0.8);
                            canvas.toBlob((blob) => {
                                const webpFile = new File([blob], `${file.name.split('.')[0]}.webp`, { 
                                    type: 'image/webp' 
                                });
                                resolve({
                                    webpFile,
                                    webpDataUrl
                                });
                            }, 'image/webp', 0.8);
                        };
                    });
                };

                const results = await Promise.all(files.map(processImage));
                setImageFiles(prev => [...prev, ...results.map(r => r.webpFile)]);
                setImagePreviews(prev => [...prev, ...results.map(r => r.webpDataUrl)]);
            } catch (error) {
                console.error("Error during image compression:", error);
                Swal.fire({
                    title: 'Error!',
                    text: 'Failed to process images',
                    icon: 'error',
                    showConfirmButton: false,
                    timer: 5000
                });
            }
        }
    };

    const removeExistingImage = (index) => {
        const removedImage = existingImages[index];
        setRemovedImages(prev => [...prev, removedImage]);
        setExistingImages(prev => prev.filter((_, i) => i !== index));
    };

    const onSubmit = async (values) => {
        try {
            if (!bulletin || !bulletin.id) {
                throw new Error('Bulletin ID is missing');
            }

            const formData = new FormData();
            formData.append('header', values.header);
            formData.append('event_date', values.event_date);
            formData.append('body', values.body);
            formData.append('bulletin_status', values.bulletin_status);
            // Include bulletin ID for update
            formData.append('bulletinId', bulletin.id);
            
            // Add any images to keep (not removed)
            if (existingImages.length > 0) {
                formData.append('existingImages', existingImages.map(img => img.file_name).join(','));
            }

            // Add any images to remove
            if (removedImages.length > 0) {
                formData.append('removedImages', removedImages.map(img => img.file_name).join(','));
            }
            
            // Append multiple new files
            imageFiles.forEach((file) => {
                formData.append('images', file);
            });

            const token = localStorage.getItem('token');
            const response = await axios.put(`${API_URL}/bulletin/${bulletin.id}`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            const data = response.data;
            setImageFiles([]);
            setImagePreviews([]);
            setRemovedImages([]);

            if (typeof onSuccess === 'function') {
                onSuccess(data);
            }

            onClose();

            Swal.fire({
                title: 'Success!',
                text: data.message || 'Bulletin updated successfully',
                icon: 'success',
                showConfirmButton: false,
                timer: 5000
            });
        } catch (error) {
            console.error('Error updating bulletin:', error);
            Swal.fire({
                title: 'Error!',
                text: error.response?.data?.error || error.message,
                icon: 'error',
                showConfirmButton: false,
                timer: 5000
            });
        }
    };

    return (
        <>
            <div id="static-modal" data-modal-backdrop="static" tabIndex="-1" aria-hidden="true" className={`${isOpen ? 'flex' : 'hidden'} bg-black bg-opacity-50 overflow-y-auto overflow-x-hidden fixed right-0 left-0 z-50 justify-center items-center w-full md:inset-0 max-sm:h-[calc(100%-1rem)] max-h-full`}>
                <div className="relative p-4 w-full max-w-2xl max-h-full">
                    <div className="relative bg-white rounded-lg shadow">
                        <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t">
                            <h3 className="text-xl font-semibold text-gray-900">
                                Edit Bulletin
                            </h3>
                            <button
                                type="button"
                                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
                                onClick={onClose}
                            >
                                <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                                </svg>
                                <span className="sr-only">Close modal</span>
                            </button>
                        </div>
                        <div className="p-4 md:p-5 space-y-4">
                            <form onSubmit={(e) => handleSubmit(e, onSubmit)}>
                                <div className="mb-4 w-full">
                                    <label htmlFor="header" className="block text-sm font-medium text-gray-700">Header</label>
                                    <input 
                                        type="text" 
                                        id="header" 
                                        name='header'
                                        value={values.header} 
                                        onChange={handleChange} 
                                        className="form-input mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-green-800 focus:border-green-800"
                                    />
                                    {errors.header && (
                                        <p className="text-red-600">{errors.header}</p>
                                    )}
                                </div>

                                <div className="mb-4 w-full">
                                    <label htmlFor="event_date" className="block text-sm font-medium text-gray-700">Event Date</label>
                                    <input 
                                        type="date" 
                                        id="event_date" 
                                        name='event_date'
                                        value={values.event_date} 
                                        onChange={handleChange} 
                                        className="form-input mt-1 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-green-800 focus:border-green-800"
                                    />
                                    {errors.event_date && (
                                        <p className="text-red-600">{errors.event_date}</p>
                                    )}
                                </div>   

                                <div className="mb-4 w-full">
                                    <label htmlFor="body" className="block text-sm font-medium text-gray-700">Body</label>
                                    <textarea
                                        id="body"
                                        name="body"
                                        value={values.body} 
                                        onChange={handleChange} 
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block w-full p-2.5 mt-2"
                                        rows="3"
                                        placeholder='Use [Enter space] for separation in the next line.'
                                    ></textarea>
                                    {errors.body && (
                                        <p className="text-red-600">{errors.body}</p>
                                    )}
                                </div>    

                                <div className="mb-4 w-full">
                                    <label htmlFor="bulletin_status" className="block text-sm font-medium text-gray-700">Status</label>
                                    <select
                                        id="bulletin_status"
                                        name="bulletin_status"
                                        value={values.bulletin_status} 
                                        onChange={handleChange}
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block w-full p-2.5"
                                    >
                                        <option value="0">Active</option>
                                        <option value="1">Deactivated</option>
                                    </select>
                                    {errors.bulletin_status && (
                                        <p className="text-red-600">{errors.bulletin_status}</p>
                                    )}
                                </div>    

                                <div className="mb-4 w-full">
                                    <div className="flex items-center justify-center w-full">
                                        <label htmlFor="images" className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50">
                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                <svg className="w-8 h-8 py-2 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                                                </svg>
                                                <p className="mb-2 text-sm text-gray-500">Click to upload</p>
                                                <p className="text-xs text-gray-500">PNG, JPG or WebP (Multiple files allowed)</p>
                                            </div>
                                            <input 
                                                id="images" 
                                                type="file" 
                                                accept="image/*" 
                                                multiple 
                                                className="hidden" 
                                                onChange={handleImageUpload} 
                                            />
                                        </label>
                                    </div>

                                    {imagePreviews.length > 0 && (
                                        <div className="mt-2">
                                            <h4 className="text-sm font-medium text-gray-700 mb-1">New Images</h4>
                                            <div className="grid grid-cols-5 gap-2">
                                                {imagePreviews.map((preview, index) => (
                                                    <div key={`new-${index}`} className="relative">
                                                        <img 
                                                            src={preview} 
                                                            alt={`New ${index + 1}`} 
                                                            className="h-24 w-full object-cover rounded" 
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setImageFiles(prev => prev.filter((_, i) => i !== index));
                                                                setImagePreviews(prev => prev.filter((_, i) => i !== index));
                                                            }}
                                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {existingImages.length > 0 && (
                                        <div className="mt-2">
                                            <h4 className="text-sm font-medium text-gray-700 mb-1">Existing Images</h4>
                                            <div className="grid grid-cols-5 gap-2">
                                            {existingImages.map((img, index) => (
                                                <div key={`existing-${index}`} className="relative">
                                                <img 
                                                    src={`/uploads/bulletin/${img.file_name}`} 
                                                    alt={`Existing ${index + 1}`} 
                                                    className="h-24 w-full object-cover rounded" 
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeExistingImage(index)}
                                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                                </div>
                                            ))}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {errors.file_name && (
                                        <p className="text-red-600 mt-2">{errors.file_name}</p>
                                    )}
                                </div>
                                
                                <div className="flex items-center p-4 md:p-5 border-t border-gray-200 rounded-b">
                                    <button 
                                        type="submit" 
                                        className="text-white bg-[var(--customRed)] hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "Updating..." : "Update"}
                                    </button>
                                    <button type="button" className="py-2.5 px-5 ms-3 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-red-700 focus:z-10 focus:ring-4 focus:ring-gray-100" onClick={onClose}>Close</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditBulletinModal;